const COLLECTION_MULTI_ADD_RESET_KEY = 'collection-multi-add:reset';

function getCollectionMultiAddRoot() {
  return document.querySelector('collection-multi-add');
}

function scheduleCollectionMultiAddReset() {
  window.setTimeout(() => {
    getCollectionMultiAddRoot()?.resetSelection();
  }, 0);
}

function shouldResetCollectionMultiAdd(event) {
  if (sessionStorage.getItem(COLLECTION_MULTI_ADD_RESET_KEY)) {
    return true;
  }

  if (!event?.persisted) {
    return false;
  }

  const nav = performance.getEntriesByType('navigation')[0];
  return nav?.type === 'back_forward' && /\/cart(\?|$|\/)/.test(document.referrer);
}

function handleCollectionMultiAddPageShow(event) {
  if (!getCollectionMultiAddRoot()) return;
  if (!shouldResetCollectionMultiAdd(event)) return;

  sessionStorage.removeItem(COLLECTION_MULTI_ADD_RESET_KEY);
  scheduleCollectionMultiAddReset();
}

window.addEventListener('pageshow', handleCollectionMultiAddPageShow);

if (!customElements.get('collection-multi-add-item')) {
  customElements.define(
    'collection-multi-add-item',
    class CollectionMultiAddItem extends HTMLElement {
      connectedCallback() { 
        this.variantSelect = this.querySelector('.collection-multi-add-item__variant-select');
        this.quantityRoot = this.querySelector('[data-quantity-root]');
        this.soldOutEl = this.querySelector('[data-sold-out]');
        this.quantityInput = this.querySelector('.quantity__input');
        this.hiddenVariantInput = this.querySelector('input[type="hidden"][name="id"]');
        this.pricingRestricted = this.dataset.pricingRestricted === 'true';

        this.variantSelect?.addEventListener('change', () => this.onVariantChange());
        this.quantityInput?.addEventListener('change', () => this.onQuantityChange());
        this.quantityInput?.addEventListener('input', () => this.onQuantityChange());

        if (this.variantSelect) {
          this.selectFirstAvailableVariant();
        }

        this.syncAvailabilityState();
      }

      notifyChange() {
        this.dispatchEvent(new CustomEvent('collection-multi-add:change', { bubbles: true }));
      }

      onQuantityChange() {
        this.updateSelectedState();
        this.notifyChange();
      }

      getQuantityValue() {
        if (!this.quantityInput || this.quantityRoot?.classList.contains('hidden')) {
          return 0;
        }

        const value = parseInt(this.quantityInput.value, 10);
        return Number.isNaN(value) ? 0 : value;
      }

      isSelected() {
        return this.getQuantityValue() > 0;
      }

      updateSelectedState() {
        this.classList.toggle('collection-multi-add-item--selected', this.isSelected());
      }

      selectFirstAvailableVariant() {
        const firstAvailable = [...this.variantSelect.options].find((option) => option.dataset.available === 'true');
        if (firstAvailable) {
          firstAvailable.selected = true;
        }
      }

      syncAvailabilityState(options = {}) {
        if (this.pricingRestricted || this.dataset.disabled === 'true') {
          this.setQuantityValue(0, { silent: true });
          return;
        }

        const option = this.getSelectedOption();
        const isAvailable = this.isVariantAvailable(option);

        this.quantityRoot?.classList.toggle('hidden', !isAvailable);
        this.soldOutEl?.classList.toggle('hidden', isAvailable);

        if (this.quantityInput && option) {
          this.applyQuantityRules(option, { preserveQuantity: options.preserveQuantity });
        }

        this.updateSelectedState();

        if (!options.silent) {
          this.notifyChange();
        }
      }

      onVariantChange() {
        this.syncAvailabilityState();
      }

      getSelectedOption() {
        if (this.variantSelect) {
          return this.variantSelect.selectedOptions[0];
        }

        if (this.hiddenVariantInput) {
          return {
            value: this.hiddenVariantInput.value,
            dataset: this.hiddenVariantInput.dataset,
            disabled: this.hiddenVariantInput.dataset.available !== 'true',
          };
        }

        return null;
      }

      isVariantAvailable(option) {
        if (!option) return false;
        if (this.pricingRestricted) return false;
        return option.dataset?.available === 'true' && !option.disabled;
      }

      applyQuantityRules(option, options = {}) {
        const variantMin = parseInt(option.dataset.min || '1', 10);
        const step = option.dataset.step || '1';
        const max = option.dataset.max;

        this.quantityInput.min = '0';
        this.quantityInput.dataset.min = String(variantMin);
        this.quantityInput.step = step;
        this.quantityInput.dataset.index = option.value;

        if (max) {
          this.quantityInput.max = max;
          this.quantityInput.dataset.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
          this.quantityInput.removeAttribute('data-max');
        }

        if (!options.preserveQuantity) {
          this.setQuantityValue(0, { silent: true });
        } else {
          this.quantityInput
            .closest('quantity-input')
            ?.validateQtyRules?.();
        }
      }

      setQuantityValue(value, options = {}) {
        if (!this.quantityInput) return;

        this.quantityInput.value = value;
        this.quantityInput
          .closest('quantity-input')
          ?.validateQtyRules?.();
        this.updateSelectedState();

        if (!options.silent) {
          this.notifyChange();
        }
      }

      getVariantId() {
        if (this.variantSelect) return this.variantSelect.value;
        return this.hiddenVariantInput?.value;
      }

      getVariantMinQuantity() {
        const option = this.getSelectedOption();
        return parseInt(option?.dataset?.min || this.quantityInput?.dataset?.min || '1', 10);
      }

      validateQuantity() {
        const quantity = this.getQuantityValue();

        if (quantity === 0) {
          return { valid: false, message: '' };
        }

        if (!this.quantityInput || this.quantityRoot?.classList.contains('hidden')) {
          return { valid: false, message: '' };
        }

        const variantMin = this.getVariantMinQuantity();
        const step = parseInt(this.quantityInput.step || 1, 10);
        const max = this.quantityInput.max ? parseInt(this.quantityInput.max, 10) : null;
        const strings = window.collectionMultiAddStrings || {};

        if (quantity < variantMin) {
          return { valid: false, message: strings.minError?.replace('[min]', variantMin) || '' };
        }

        if (max !== null && quantity > max) {
          return { valid: false, message: strings.maxError?.replace('[max]', max) || '' };
        }

        if (quantity % step !== 0) {
          return { valid: false, message: strings.stepError?.replace('[step]', step) || '' };
        }

        return { valid: true, quantity };
      }

      getSelectedItem() {
        if (this.pricingRestricted || this.dataset.disabled === 'true') {
          return null;
        }

        const quantity = this.getQuantityValue();
        if (quantity === 0) {
          return null;
        }

        const option = this.getSelectedOption();
        if (!this.isVariantAvailable(option)) {
          return null;
        }

        const variantId = this.getVariantId();
        const { valid, quantity: validQuantity, message } = this.validateQuantity();

        if (!variantId || !valid) {
          return { error: message };
        }

        return {
          id: parseInt(variantId, 10),
          quantity: validQuantity,
        };
      }

      resetQuantity() {
        this.setQuantityValue(0, { silent: true });
      }
    }
  );
}

if (!customElements.get('collection-multi-add')) {
  customElements.define(
    'collection-multi-add',
    class CollectionMultiAdd extends HTMLElement {
      connectedCallback() {
        this.bars = [...this.querySelectorAll('.collection-multi-add__bar')];
        this.submitButtons = [...this.querySelectorAll('.collection-multi-add__submit')];
        this.countElements = [...this.querySelectorAll('[data-multi-add-count], .collection-multi-add__count')];
        this.errorElement = this.querySelector('.collection-multi-add__error');
        this.spinners = [...this.querySelectorAll('.collection-multi-add__bar .loading__spinner')];
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.submitButtons.forEach((button) => button.addEventListener('click', () => this.addSelected()));
        this.addEventListener('collection-multi-add:change', () => this.updateBar());
        this.bindBottomBarAlign();

        if (sessionStorage.getItem(COLLECTION_MULTI_ADD_RESET_KEY)) {
          sessionStorage.removeItem(COLLECTION_MULTI_ADD_RESET_KEY);
          scheduleCollectionMultiAddReset();
        } else {
          this.updateBar();
        }
      }

      disconnectedCallback() {
        if (this.syncBottomBarAlign) {
          window.removeEventListener('resize', this.syncBottomBarAlign);
          window.removeEventListener('scroll', this.syncBottomBarAlign);
        }
        this.bottomBarAlignObserver?.disconnect();
      }

      bindBottomBarAlign() {
        this.bottomBar = this.querySelector('.collection-multi-add__bar--bottom');
        this.productGrid = this.querySelector('#product-grid');
        if (!this.bottomBar || !this.productGrid) return;

        this.syncBottomBarAlign = () => {
          window.requestAnimationFrame(() => {
            if (!this.bottomBar || !this.productGrid) return;
            const rect = this.productGrid.getBoundingClientRect();
            this.bottomBar.style.left = `${rect.left}px`;
            this.bottomBar.style.width = `${rect.width}px`;
            this.bottomBar.style.right = 'auto';
          });
        };

        this.syncBottomBarAlign();
        window.addEventListener('resize', this.syncBottomBarAlign);
        window.addEventListener('scroll', this.syncBottomBarAlign, { passive: true });

        if (typeof ResizeObserver !== 'undefined') {
          this.bottomBarAlignObserver = new ResizeObserver(this.syncBottomBarAlign);
          this.bottomBarAlignObserver.observe(this.productGrid);

          const gridContainer = this.productGrid.closest('.product-grid-container');
          if (gridContainer) {
            this.bottomBarAlignObserver.observe(gridContainer);
          }
        }
      }

      getItems() {
        return [...this.querySelectorAll('collection-multi-add-item')];
      }

      getSelectedItems() {
        const items = [];
        let errorMessage = '';

        for (const item of this.getItems()) {
          const selected = item.getSelectedItem();
          if (!selected) continue;
          if (selected.error) {
            errorMessage = selected.error;
            continue;
          }
          items.push({ id: selected.id, quantity: selected.quantity });
        }

        return { items, errorMessage };
      }

      updateBar() {
        const { items } = this.getSelectedItems();
        const count = items.length;
        const strings = window.collectionMultiAddStrings || {};

        if (!this.bars.length || !this.submitButtons.length || !this.countElements.length) return;

        const hasSelection = count > 0;
        const countLabel = (strings.selectedCount || '[count] selected').replace('[count]', count);

        this.bars.forEach((bar) => {
          bar.classList.toggle('collection-multi-add__bar--visible', hasSelection);
          bar.toggleAttribute('hidden', !hasSelection);
        });
        this.classList.toggle('collection-multi-add--has-selection', hasSelection);
        this.submitButtons.forEach((button) => {
          button.disabled = !hasSelection;
        });
        this.countElements.forEach((element) => {
          element.textContent = countLabel;
        });

        if (hasSelection && this.errorElement) {
          this.errorElement.hidden = true;
        }

        this.syncBottomBarAlign?.();
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
        this.bars.forEach((bar) => {
          bar.classList.add('collection-multi-add__bar--visible');
          bar.removeAttribute('hidden');
        });
        this.classList.add('collection-multi-add--has-selection');
        this.syncBottomBarAlign?.();
      }

      setLoading(loading) {
        this.submitButtons.forEach((button) => button.classList.toggle('loading', loading));
        if (!loading) {
          this.updateBar();
        } else {
          this.submitButtons.forEach((button) => button.setAttribute('disabled', ''));
        }
        this.spinners.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
      }

      resetSelection() {
        this.getItems().forEach((item) => item.resetQuantity());
        this.updateBar();
      }

      addSelected() {
        const strings = window.collectionMultiAddStrings || {};
        const { items, errorMessage } = this.getSelectedItems();

        if (errorMessage) {
          this.showError(errorMessage);
          return;
        }

        if (!items.length) {
          this.showError(strings.empty || 'Set quantity on at least one product to add.');
          return;
        }

        this.setLoading(true);
        if (this.errorElement) {
          this.errorElement.hidden = true;
          this.errorElement.textContent = '';
        }

        const payload = { items };

        if (this.cart?.getSectionsToRender) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname + window.location.search;
        }

        fetch(`${routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          method: 'POST',
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'collection-multi-add',
                errors: response.errors || response.description,
                message: response.message,
              });
              this.showError(response.description || response.message || strings.error);
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'collection-multi-add',
              cartData: response,
            });

            this.resetSelection();
            sessionStorage.setItem(COLLECTION_MULTI_ADD_RESET_KEY, '1');
            window.location.href = window.routes.cart_url;
          })
          .catch((error) => {
            console.error(error);
            this.showError(strings.error || 'Unable to add items to cart.');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }
    }
  );
}
